/**
 * useMobilePostReview Hook
 * Custom hook for MobilePostReview component API integration
 * 
 * Features:
 * - Posts loading with campaign filtering
 * - Real-time post approval/rejection
 * - AI regeneration (content, image, full)
 * - Post text editing
 * - Image upload handling
 * - Optimized mobile performance
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import useApi from './useApi';
import postService from '../services/PostService';

const MOBILE_POST_REVIEW_PAGE_SIZE = 24;

const useMobilePostReview = (campaignId = null) => {
  // State management
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  
  // API and service setup
  const api = useApi();
  const initialized = useRef(false);
  const [isServiceReady, setIsServiceReady] = useState(false);
  
  // Initialize PostService with API hook
  useEffect(() => {
    if (api && !initialized.current) {
      postService.initialize(api);
      initialized.current = true;
      console.log('[useMobilePostReview] PostService initialized');
      setIsServiceReady(true);
    }
  }, [api]);

  /**
   * Load posts for review (draft status by default for mobile review)
   */
  const loadPosts = useCallback(async (options = {}) => {
    if (!initialized.current) {
      console.warn('[useMobilePostReview] Service not initialized yet');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const filters = {
        // Load both draft and scheduled posts for mobile review
        // Don't filter by status - let the user see all posts
        campaign_id: campaignId,
        limit: MOBILE_POST_REVIEW_PAGE_SIZE,
        offset: currentPage * MOBILE_POST_REVIEW_PAGE_SIZE,
        ...options
      };

      console.log('[useMobilePostReview] Loading posts with filters:', filters);
      const response = await postService.listPosts(filters);

      let newPosts = response.posts || [];

      // Normalize post IDs - ensure 'id' field exists (backend uses '_id')
      newPosts = newPosts.map(post => ({
        ...post,
        id: post.id || post._id // Normalize _id to id for consistent access
      }));

      // Sort posts chronologically by scheduled time (ascending)
      const parseScheduled = (p) => {
        const v = p?.scheduledDate || p?.scheduled_time || null;
        if (!v) return 0;
        try {
          if (typeof v === 'object' && v.$date) return new Date(v.$date).getTime();
          return new Date(v).getTime();
        } catch (e) {
          return 0;
        }
      };
      newPosts = [...newPosts].sort((a, b) => parseScheduled(a) - parseScheduled(b));
      
      if (options.reset || currentPage === 0) {
        setPosts(newPosts);
      } else {
        setPosts(prev => [...prev, ...newPosts]);
      }
      
      // Update pagination state
      setHasMore(response.pagination?.has_next || false);
      setCurrentPage(prev => options.reset ? 0 : prev);
      
      console.log('[useMobilePostReview] Posts loaded:', {
        count: newPosts.length,
        total: response.pagination?.total_count || 0,
        hasMore: response.pagination?.has_next || false
      });
      
      return response;
    } catch (err) {
      console.error('[useMobilePostReview] Failed to load posts:', err);
      setError(err.message || 'Failed to load posts');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [campaignId, currentPage, initialized]);

  /**
   * Load next page of posts
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) return;
    
    setCurrentPage(prev => prev + 1);
    return loadPosts();
  }, [hasMore, isLoading, loadPosts]);

  /**
   * Refresh posts (reload first page)
   */
  const refresh = useCallback(async () => {
    setCurrentPage(0);
    return loadPosts({ reset: true });
  }, [loadPosts]);

  /**
   * Approve a post (change status to 'scheduled')
   */
  const approvePost = useCallback(async (post) => {
    if (!initialized.current) throw new Error('Service not initialized');

    try {
      const postId = post.id || post._id; // Handle both id formats
      console.log('[useMobilePostReview] Approving post:', { postId, post });

      // Use updatePostStatus like desktop does - change to 'scheduled' status
      const response = await postService.updatePostStatus(postId, 'scheduled');

      // Update post in local state with new status
      setPosts(prev => prev.map(p =>
        (p.id === postId || p._id === postId)
          ? { ...p, ...response.post, status: 'scheduled', id: postId }
          : p
      ));

      // Emit event to sync with calendar (same as desktop)
      window.dispatchEvent(new CustomEvent('postDirectUpdate', {
        detail: {
          postId: postId,
          updatedPost: {
            ...response.post,
            status: 'scheduled',
            id: postId
          }
        }
      }));

      console.log('[useMobilePostReview] Post approved successfully:', postId);
      return response;
    } catch (err) {
      console.error('[useMobilePostReview] Failed to approve post:', err);
      throw err;
    }
  }, [initialized]);

  /**
   * Reject a post (change status to 'draft')
   */
  const rejectPost = useCallback(async (post) => {
    if (!initialized.current) throw new Error('Service not initialized');

    try {
      const postId = post.id || post._id; // Handle both id formats
      console.log('[useMobilePostReview] Rejecting post:', { postId, post });

      // Use updatePostStatus like desktop does - change to 'draft' status
      const response = await postService.updatePostStatus(postId, 'draft');

      // Update post in local state with new status
      setPosts(prev => prev.map(p =>
        (p.id === postId || p._id === postId)
          ? { ...p, ...response.post, status: 'draft', id: postId }
          : p
      ));

      // Emit event to sync with calendar (same as desktop)
      window.dispatchEvent(new CustomEvent('postDirectUpdate', {
        detail: {
          postId: postId,
          updatedPost: {
            ...response.post,
            status: 'draft',
            id: postId
          }
        }
      }));

      console.log('[useMobilePostReview] Post rejected successfully:', postId);
      return response;
    } catch (err) {
      console.error('[useMobilePostReview] Failed to reject post:', err);
      throw err;
    }
  }, [initialized]);

  /**
   * Delete a post permanently
   */
  const deletePost = useCallback(async (postOrId) => {
    if (!initialized.current) throw new Error('Service not initialized');

    const postId = typeof postOrId === 'string'
      ? postOrId
      : postOrId?.id || postOrId?._id;

    if (!postId) {
      throw new Error('Invalid post identifier');
    }

    try {
      console.log('[useMobilePostReview] Deleting post:', { postId });
      const response = await postService.deletePost(postId);

      setPosts(prev => prev.filter(p => {
        const id = p?.id || p?._id;
        return id !== postId;
      }));

      window.dispatchEvent(new CustomEvent('postDeleted', {
        detail: { postId }
      }));

      return response;
    } catch (err) {
      console.error('[useMobilePostReview] Failed to delete post:', err);
      throw err;
    }
  }, [initialized]);

  /**
   * Regenerate post using AI
   */
  const regeneratePost = useCallback(async (post, type = 'full', options = {}) => {
    if (!initialized.current) throw new Error('Service not initialized');

    setIsRegenerating(true);

    try {
      const postId = post.id || post._id; // Handle both id formats
      console.log('[useMobilePostReview] Regenerating post:', {
        postId,
        type,
        options
      });

      let response;

      switch (type) {
        case 'content':
        case 'text':
          console.log('[useMobilePostReview] Calling regenerateContent');
          response = await postService.regenerateContent(postId, options);
          break;
        case 'image':
          console.log('[useMobilePostReview] Calling regenerateImage');
          response = await postService.regenerateImage(postId, options);
          break;
        case 'full':
        default:
          console.log('[useMobilePostReview] Calling regeneratePost (full)');
          response = await postService.regeneratePost(postId, options);
          break;
      }

      console.log('[useMobilePostReview] Service response:', response);

      // Update post in local state
      setPosts(prev => prev.map(p =>
        (p.id === postId || p._id === postId)
          ? { ...p, ...response.post, id: postId }
          : p
      ));

      console.log('[useMobilePostReview] Post regenerated successfully:', {
        postId,
        type,
        status: response.status
      });
      
      return response;
    } catch (err) {
      console.error('[useMobilePostReview] Failed to regenerate post:', err);
      throw err;
    } finally {
      setIsRegenerating(false);
    }
  }, [initialized]);

  /**
   * Edit post text content
   */
  const editPostText = useCallback(async (post, newText) => {
    if (!initialized.current) throw new Error('Service not initialized');

    try {
      const postId = post.id || post._id; // Handle both id formats
      console.log('[useMobilePostReview] Editing post text:', {
        postId,
        newTextLength: newText.length
      });

      const response = await postService.updatePost(postId, {
        content: newText
      });

      // Update post in local state
      setPosts(prev => prev.map(p =>
        (p.id === postId || p._id === postId)
          ? { ...p, content: newText, description: newText, id: postId }
          : p
      ));

      console.log('[useMobilePostReview] Post text edited successfully:', postId);
      return response;
    } catch (err) {
      console.error('[useMobilePostReview] Failed to edit post text:', err);
      throw err;
    }
  }, [initialized]);

  /**
   * Handle image upload for a post
   */
  const handleImageUpload = useCallback(async (post, imageIndex, file) => {
    if (!initialized.current) throw new Error('Service not initialized');
    if (!file || !(file instanceof File)) throw new Error('Invalid file provided');

    try {
      const postId = post.id || post._id; // Handle both id formats
      console.log('[useMobilePostReview] Uploading image:', {
        postId,
        imageIndex,
        fileName: file.name,
        fileSize: file.size
      });

      // Create media updates array - preserve existing media and replace at specific index
      const currentMedia = post.media_urls || post.images || [];
      const mediaUpdates = [];

      // Preserve existing media before the target index
      for (let i = 0; i < Math.max(imageIndex, currentMedia.length + 1); i++) {
        if (i === imageIndex) {
          // Replace with new file at target index
          mediaUpdates.push({
            type: 'file',
            file: file
          });
        } else if (i < currentMedia.length && currentMedia[i]) {
          // Keep existing media by reference
          mediaUpdates.push({
            type: 'url',
            url: currentMedia[i]
          });
        }
      }

      const response = await postService.updatePost(postId, {}, mediaUpdates);

      // Update post in local state
      setPosts(prev => prev.map(p =>
        (p.id === postId || p._id === postId)
          ? { ...p, ...response.post, id: postId }
          : p
      ));

      console.log('[useMobilePostReview] Image uploaded successfully:', {
        postId,
        imageIndex,
        newMediaCount: response.post?.media_urls?.length || 0
      });
      
      return response;
    } catch (err) {
      console.error('[useMobilePostReview] Failed to upload image:', err);
      throw err;
    }
  }, [initialized]);

  /**
   * Navigate to post edit page
   */
  const editPost = useCallback((post) => {
    const postId = post.id || post._id; // Handle both id formats
    console.log('[useMobilePostReview] Opening post edit page:', postId);
    // This would typically navigate to a detailed edit page
    // For now, we'll just log the action
    // In a full implementation, this would use React Router or similar
  }, []);

  /**
   * Apply a version to a post (update via API)
   * Switches the post to use a specific version from its history
   * @param {Object} post - Post object
   * @param {Object} version - Version object from post.versions array
   * @returns {Promise<Object>} Updated post
   */
  const applyPostVersion = useCallback(async (post, version) => {
    if (!initialized.current) throw new Error('Service not initialized');

    try {
      const postId = post.id || post._id;
      console.log('[useMobilePostReview] Applying version to post:', {
        postId,
        versionId: version.version_id
      });

      // Update post via API with the version's content
      const response = await postService.updatePost(postId, {
        content: version.content,
        hashtags: version.hashtags || post.hashtags,
        mentions: version.mentions || post.mentions
      });

      // Update post in local state
      setPosts(prev => prev.map(p =>
        (p.id === postId || p._id === postId)
          ? { ...p, ...response.post, id: postId }
          : p
      ));

      console.log('[useMobilePostReview] Version applied successfully:', postId);
      return response;
    } catch (err) {
      console.error('[useMobilePostReview] Failed to apply version:', err);
      throw err;
    }
  }, [initialized]);

  // Load posts when service becomes ready or campaign changes
  useEffect(() => {
    if (isServiceReady) {
      loadPosts({ reset: true });
    }
  }, [isServiceReady, campaignId, loadPosts]);

  // Clear error when posts change
  useEffect(() => {
    if (error && posts.length > 0) {
      setError(null);
    }
  }, [posts.length, error]);

  return {
    // Data
    posts,
    isLoading,
    isRegenerating,
    error,
    hasMore,
    currentPage,

    // Actions
    loadPosts,
    loadMore,
    refresh,
    approvePost,
    rejectPost,
    deletePost,
    regeneratePost,
    editPostText,
    handleImageUpload,
    editPost,

    // Version Management
    applyPostVersion,

    // Utilities
    clearError: () => setError(null),

    // Meta
    initialized: initialized.current
  };
};

export default useMobilePostReview;
